import { Link, useParams } from "react-router-dom";
import { Copy, MonitorPlay, Radio, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCompanion } from "@/components/companion/useCompanion";
import { Lobby, FORMATS } from "@/components/companion/Lobby";
import { Planechase } from "@/components/companion/Planechase";
import { Archenemy } from "@/components/companion/Archenemy";
import { Vanguard } from "@/components/companion/Vanguard";
import { TurnTracker } from "@/components/companion/TurnTracker";
import { EffectsZone, Prompts } from "@/components/companion/EffectsZone";

function History({ s }) {
  return (
    <aside className="panel max-h-[70vh] overflow-y-auto p-4" data-testid="session-history">
      <p className="eyebrow mb-2">Session history</p>
      <ol className="space-y-1.5 text-xs">{[...s.history].reverse().map((h, i) => (
        <li key={i} className={h.override ? "text-amber-300" : "text-slate-300"} data-testid={`history-${i}`}>
          <span className="font-mono text-slate-500">T{h.turn} · {h.at.slice(11, 16)} · @{h.actor}</span><br />{h.message}</li>))}</ol>
    </aside>
  );
}

function Header({ s, live, call, act }) {
  const active = s.seats.find((x) => x.seat_id === s.game.active_seat);
  const copy = () => { navigator.clipboard?.writeText(s.code); toast.success("Join code copied"); };
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <p className="eyebrow">Multiverse Companion · {FORMATS[s.base_format]}</p>
        <h1 className="font-display text-3xl text-amber-100 sm:text-4xl" data-testid="session-name">{s.name}</h1>
        <p className="mt-2 flex flex-wrap items-center gap-3 text-sm text-slate-400">
          <button onClick={copy} className="flex items-center gap-1 rounded border border-aether-500/60 px-2 py-1 font-mono text-lg tracking-[0.3em] text-aether-200" data-testid="session-code">{s.code}<Copy className="h-3 w-3" /></button>
          <span className={`flex items-center gap-1 text-xs ${live ? "text-emerald-300" : "text-amber-300"}`} data-testid="live-indicator"><Radio className="h-3 w-3" />{live ? "Live" : "Syncing every 2 s"}</span>
          <span className="text-xs">{s.seats.length} seats · {s.spectator_count} watching · you are {s.role}</span>
          {s.status === "active" && active && <span className="text-xs text-amber-200" data-testid="active-player">Turn {s.game.turn}: {active.name}</span>}
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Link to={`/companion/s/${s.code}/display`} target="_blank" className="btn-outline" data-testid="open-display-link"><MonitorPlay className="h-4 w-4" />Shared display</Link>
        {s.can_undo && <button className="btn-outline" onClick={() => act("undo")} data-testid="undo-button"><Undo2 className="h-4 w-4" />Undo</button>}
        {s.is_host && s.status === "active" && <select className="field w-auto" value={s.game.active_seat || ""} onChange={(e) => act("set_active", { seat_id: e.target.value })} data-testid="set-active-select">{s.seats.map((x) => <option key={x.seat_id} value={x.seat_id}>{`Active: ${x.name}`}</option>)}</select>}
        {s.is_host && s.status !== "ended" && <button className="btn-ghost text-red-300" onClick={() => window.confirm("End this session for everyone?") && call(() => api.post(`/companion/sessions/${s.code}/end`).then(() => api.get(`/companion/sessions/${s.code}`)), "Session ended")} data-testid="end-session-button">End</button>}
      </div>
    </div>
  );
}

export default function CompanionSession() {
  const { code } = useParams();
  const { s, error, loading, live, act, call } = useCompanion(code.toUpperCase());
  if (loading) return <div className="container-x py-16 text-slate-500">Opening the portal…</div>;
  if (error || !s) return <div className="container-x py-16 text-slate-400" data-testid="session-error">{error?.response?.status === 403 ? <>You're not in this session. <Link to={`/companion?code=${code}`} className="text-aether-300">Join with the code</Link>.</> : "Session not found or expired."}</div>;
  const activeMine = (s.active_unit || [s.game.active_seat]).some((x) => s.my_seats.includes(x));
  return (
    <div className="container-x space-y-8 py-10" data-testid="companion-session">
      <Header s={s} live={live} call={call} act={act} />
      {s.rules.custom_rules && s.status !== "lobby" && <p className="panel p-3 text-sm text-amber-200" data-testid="house-rules">House rules: {s.rules.custom_rules}</p>}
      {s.status === "lobby" ? <Lobby s={s} call={call} /> : (
        <div className="grid gap-8 xl:grid-cols-[1fr_320px]">
          <div className="space-y-10">
            {s.status === "ended" && <p className="panel p-3 text-sm text-slate-300" data-testid="session-ended">This session has ended. It is deleted 7 days after the last activity.</p>}
            {s.status === "active" && <TurnTracker s={s} act={act} />}
            <Prompts s={s} act={act} />
            {s.modes.planechase && <Planechase s={s} act={act} mine={activeMine} />}
            {s.modes.archenemy && <Archenemy s={s} act={act} />}
            <EffectsZone s={s} act={act} />
            {s.modes.vanguard && <Vanguard s={s} act={act} />}
          <p className="text-xs text-slate-500" data-testid="automation-note">Automated: card movement, die rolls, counters, turn and step order. Reminders: structured conditions on effects, planes and ongoing schemes. Manual: every ability, payment and condition. Players confirm them at the table.</p>
          </div>
          <History s={s} />
        </div>
      )}
    </div>
  );
}
